/* How many units the main building actually turns out in a round.

   The panel computed `PRODUCTION_CAPACITY_PER_LEVEL * level` for both teams, and the server does
   not: `build_from_hatchery` multiplies the hatchery's capacity by Outbreak, while
   `build_from_command_center` leaves the command centre's alone. That asymmetry is deliberate and
   matches what the two researches claim — Outbreak is "+hatchery production capacity", Prosperity is
   "+workers grown per command centre level" (since 2026-09-29), so the human bonus lands on the
   growth rate instead.

   The effect of missing it: a zombie holding Outbreak 3 with a level-3 hatchery can queue 4800, and
   the panel said 3000 and warned that the rest would carry over. Not blocking — the button still
   sends — but the research they bought was invisible, and the number was simply wrong.

   As with `buildingCosts.js`, no balance number is written here: they come from
   `GET /game-config/`. */

import { capacityMultiplier } from "src/utils/gameModifiers"
import { scale } from "src/utils/ladder"

export const productionCapacityFor = ({ gameConfig, team, buildingLevel, researchLevels }) => {
  const configKey = team === "human" ? "human_config" : "zombie_config"
  const buildingKey = team === "human" ? "command_center" : "hatchery"
  const perLevel = gameConfig?.[configKey]?.BUILDINGS?.[buildingKey]?.PRODUCTION_CAPACITY_PER_LEVEL || 0
  // `scale`, not the level: requirement 34 made a level 1 worth the old level 2, and the server
  // sizes this with the same helper (`build_from_command_center` / `build_from_hatchery`).
  const base = perLevel * scale(buildingLevel)

  // Humans only get the command centre's flat capacity — Prosperity raises how fast their workers
  // grow, which is a different number on a different screen.
  if (team === "human") return base

  // The multiplier itself lives in gameModifiers, so the rule has one implementation.
  return Math.trunc(base * capacityMultiplier({ gameConfig, team, researchLevels }))
}

export default productionCapacityFor

/* How many of one unit the city can pay for *right now*, after what the rest of the draft queue
   already costs — the slider's reach (owner, 2026-09-07). S16 sent the slider to the queue cap,
   ten rounds of workers, and that made a 1500-wide bar for a city holding 250: every pixel was
   several soldiers and the thumb could not be placed. So the bar covers what the city can afford
   this round and the number field beside it is how a player queues past that, on purpose — with no
   cap at all since 2026-10-03 (owner: "Let users go over 10 rounds"): the server never had one, and
   the panel's DURATION turns red past ten rounds instead.

   `costs` is `{unitType: effective cost}` (the research discount applied), `queue` the draft
   `{unitType: number}`, `available` the workers on the cell or the meat pool. */
export const affordableNowFor = ({ unitType, costs, queue, available }) => {
  // Fractional since the discount came off the whole order (2026-09-14); only a missing or zero
  // price falls back to 1. `n × cost ≤ available` is exactly "the order's ceiling fits", since
  // `available` is whole.
  const unitCost = costs?.[unitType] > 0 ? costs[unitType] : 1
  const others = Object.entries(queue || {}).reduce(
    (sum, [type, number]) => (type === unitType ? sum : sum + (costs?.[type] || 0) * (Number(number) || 0)),
    0
  )
  return Math.max(0, Math.floor(Number((((available || 0) - others) / unitCost).toFixed(6))))
}
